import {
  readLocalPreference,
  writeLocalPreference,
} from '@/lib/local-preference'

/**
 * The dashboard's tabs, in tab-bar order. The tuple is the single source of
 * both the id union and the runtime validation, so a stored or linked value
 * can never name a tab the shell does not render.
 */
export const COACHING_TAB_IDS = [
  'overview',
  'review',
  'curriculum',
  'roadmap',
] as const

export type CoachingTabId = (typeof COACHING_TAB_IDS)[number]

/**
 * Tab-bar labels, kept beside the ids the way `seen-filter.ts` keeps
 * `SEEN_FILTER_LABEL` beside its order. Typed as a total `Record`, so adding a
 * tab id without a label — or a label without an id — is a compile error, and
 * the shell can derive its bar from `COACHING_TAB_IDS` instead of restating
 * the membership and the order in a second list that can drift.
 */
export const COACHING_TAB_LABEL: Record<CoachingTabId, string> = {
  overview: 'Overview',
  review: 'Review',
  curriculum: 'Curriculum',
  roadmap: 'Roadmap',
}

/**
 * A coach-level viewing preference, like the curriculum seen-filter —
 * deliberately NOT per client, so the tab survives switching clients. A coach
 * working through a round of reviews stays on Review as they move down the
 * roster.
 */
const ACTIVE_TAB_STORAGE_KEY = 'bctci:coaching:active-tab'

const isCoachingTabId = (value: string): value is CoachingTabId =>
  (COACHING_TAB_IDS as readonly string[]).includes(value)

/**
 * The tab named by the URL fragment, if it names one. Case-insensitive: the
 * homework section links to `#curriculum` and a coach may retype a hash by
 * hand. A fragment naming anything else — a section anchor, a stale link — is
 * not an opinion about tabs, and both the reader here and the writer in
 * `persistActiveTab` leave it alone.
 */
export const hashTab = (): CoachingTabId | null => {
  if (typeof window === 'undefined') return null
  const fragment = window.location.hash.replace(/^#/, '').toLowerCase()
  return isCoachingTabId(fragment) ? fragment : null
}

const storedTab = (): CoachingTabId =>
  readLocalPreference<CoachingTabId>(
    ACTIVE_TAB_STORAGE_KEY,
    (raw) => (isCoachingTabId(raw) ? raw : null),
    'overview',
  )

/**
 * Which tab the shell opens on. An explicit hash wins — following a link to
 * `#curriculum` means "take me there now", regardless of where the coach was
 * last. Otherwise the remembered tab, otherwise Overview.
 */
export function initialActiveTab(): CoachingTabId {
  if (typeof window === 'undefined') return 'overview'
  return hashTab() ?? storedTab()
}

/**
 * Record the open tab in both places it is read from: storage, so the next
 * visit lands here, and the fragment, so the address bar is honest about what
 * is on screen and stays shareable.
 *
 * `replaceState`, never `pushState`: a tab switch is not a navigation, and
 * Back must still mean "the page before this one" rather than walking the
 * coach back through every tab they touched.
 *
 * Both writes are best-effort. The fragment is only ours to write when it is
 * empty or already names a tab — a foreign anchor belongs to whoever put it
 * there, and the router scrolls to a rendered anchor target, so overwriting it
 * would break their link to save a preference we already hold in storage. A
 * fragment already naming this tab is left as-is: history writes are
 * rate-limited by browsers (Safari throws `SecurityError` past roughly 100
 * calls per 30 seconds, which a held arrow key can reach), so redundant ones
 * spend that budget for nothing — and a refused write must not escape a
 * passive effect and take the whole admin page down with the app error
 * boundary.
 */
export function persistActiveTab(tab: CoachingTabId): void {
  if (typeof window === 'undefined') return
  writeLocalPreference(ACTIVE_TAB_STORAGE_KEY, tab)

  // hashTab is the one parser of the fragment, so reader and writer cannot
  // drift on which fragments belong to the tab system.
  const current = hashTab()
  if (current === tab) return
  if (current === null && window.location.hash !== '') return

  try {
    // A fragment-only URL resolves against the document URL, so the path and
    // query string survive untouched.
    window.history.replaceState(window.history.state, '', `#${tab}`)
  } catch {
    // History rate-limited — the tab still renders and is still remembered.
  }
}
